import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import {
  Shift,
  ShiftWithBreaks,
  BreakType,
  ShiftBreak,
  WorkerShiftAssignment,
  ShiftSummary,
  CurrentShiftAssignment,
} from "@/types/shifts";
import {
  ShiftFormData,
  BreakTypeFormData,
  ShiftBreakFormData,
  WorkerShiftAssignmentFormData,
  BulkShiftAssignmentFormData,
} from "@/schemas/shiftSchemas";

// =====================================================
// SHIFTS QUERIES
// =====================================================

export function useShifts(activeOnly = false) {
  return useQuery({
    queryKey: ["shifts", { activeOnly }],
    queryFn: async () => {
      let query = api.from("shifts").select("*").order("start_time");
      if (activeOnly) {
        query = query.eq("is_active", true);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as Shift[];
    },
  });
}

export function useShiftsSummary() {
  return useQuery({
    queryKey: ["shifts_summary"],
    queryFn: async () => {
      const { data, error } = await api
        .from("shifts_summary")
        .select("*")
        .order("start_time");
      if (error) throw error;
      return data as ShiftSummary[];
    },
  });
}

export function useShift(shiftId: string | undefined) {
  return useQuery({
    queryKey: ["shifts", shiftId],
    queryFn: async () => {
      const { data, error } = await api
        .from("shifts")
        .select("*")
        .eq("id", shiftId!)
        .single();
      if (error) throw error;
      return data as Shift;
    },
    enabled: !!shiftId,
  });
}

export function useShiftWithBreaks(shiftId: string | undefined) {
  return useQuery({
    queryKey: ["shifts", shiftId, "breaks"],
    queryFn: async () => {
      // Get shift
      const { data: shift, error: shiftError } = await api
        .from("shifts")
        .select("*")
        .eq("id", shiftId!)
        .single();
      if (shiftError) throw shiftError;

      // Get breaks with break_type info
      const { data: breaks, error: breaksError } = await api
        .from("shift_breaks")
        .select("*, break_types(*)")
        .eq("shift_id", shiftId!)
        .order("start_offset_minutes");
      if (breaksError) throw breaksError;

      // Map breaks with break_type relation
      const mappedBreaks = (breaks || []).map((b: any) => ({
        ...b,
        break_type: b.break_types,
      }));

      return { ...shift, breaks: mappedBreaks } as ShiftWithBreaks;
    },
    enabled: !!shiftId,
  });
}

// =====================================================
// BREAK TYPES QUERIES
// =====================================================

export function useBreakTypes(activeOnly = false) {
  return useQuery({
    queryKey: ["break_types", { activeOnly }],
    queryFn: async () => {
      let query = api.from("break_types").select("*").order("name");
      if (activeOnly) {
        query = query.eq("is_active", true);
      }
      const { data, error } = await query;
      if (error) throw error;
      return data as BreakType[];
    },
  });
}

// =====================================================
// SHIFT BREAKS QUERIES
// =====================================================

export function useShiftBreaks(shiftId: string | undefined) {
  return useQuery({
    queryKey: ["shift_breaks", shiftId],
    queryFn: async () => {
      const { data, error } = await api
        .from("shift_breaks")
        .select("*, break_types(*)")
        .eq("shift_id", shiftId!)
        .order("start_offset_minutes");
      if (error) throw error;
      return (data || []).map((b: any) => ({
        ...b,
        break_type: b.break_types,
      })) as ShiftBreak[];
    },
    enabled: !!shiftId,
  });
}

// =====================================================
// WORKER ASSIGNMENTS QUERIES
// =====================================================

export function useWorkerShiftAssignments(filters?: {
  workerId?: string;
  shiftId?: string;
  activeOnly?: boolean;
}) {
  return useQuery({
    queryKey: ["worker_shift_assignments", filters],
    queryFn: async () => {
      let query = api
        .from("worker_shift_assignments")
        .select("*, shifts(*), profiles!worker_shift_assignments_worker_id_fkey(id, full_name, employee_id, area)")
        .order("effective_date", { ascending: false });

      if (filters?.workerId) {
        query = query.eq("worker_id", filters.workerId);
      }
      if (filters?.shiftId) {
        query = query.eq("shift_id", filters.shiftId);
      }
      if (filters?.activeOnly) {
        query = query.is("end_date", null);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Map relations
      return (data || []).map((d: any) => ({
        ...d,
        shift: d.shifts,
        worker: d.profiles,
      })) as WorkerShiftAssignment[];
    },
  });
}

export function useCurrentShiftAssignments() {
  return useQuery({
    queryKey: ["current_shift_assignments"],
    queryFn: async () => {
      const { data, error } = await api
        .from("current_shift_assignments")
        .select("*")
        .order("full_name");
      if (error) throw error;
      return data as CurrentShiftAssignment[];
    },
  });
}

export function useWorkersWithoutShift() {
  return useQuery({
    queryKey: ["workers_without_shift"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];

      // Get all active workers
      const { data: workers, error: workersError } = await api
        .from("profiles")
        .select("id, full_name, employee_id, area")
        .eq("is_active", true)
        .order("full_name");
      if (workersError) throw workersError;

      // Get workers with current assignments
      const { data: assignments, error: assignmentsError } = await api
        .from("worker_shift_assignments")
        .select("worker_id")
        .lte("effective_date", today)
        .is("end_date", null);
      if (assignmentsError) throw assignmentsError;

      const assignedIds = new Set((assignments || []).map((a: any) => a.worker_id));

      // Filter workers without assignments
      return (workers || []).filter((w: any) => !assignedIds.has(w.id));
    },
  });
}

// =====================================================
// SHIFT MUTATIONS
// =====================================================

export function useCreateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ShiftFormData) => {
      const payload = {
        ...data,
        start_time: data.start_time + ":00",
        end_time: data.end_time + ":00",
      };
      const { error } = await api.from("shifts").insert([payload]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shifts_summary"] });
    },
  });
}

export function useUpdateShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: ShiftFormData & { id: string }) => {
      const payload = {
        ...data,
        start_time: data.start_time + ":00",
        end_time: data.end_time + ":00",
      };
      const { error } = await api.from("shifts").update(payload).eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shifts", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["shifts_summary"] });
    },
  });
}

export function useDeleteShift() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from("shifts").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shifts_summary"] });
    },
  });
}

export function useToggleShiftActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await api.from("shifts").update({ is_active }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["shifts"] });
      queryClient.invalidateQueries({ queryKey: ["shifts_summary"] });
    },
  });
}

// =====================================================
// BREAK TYPE MUTATIONS
// =====================================================

export function useCreateBreakType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BreakTypeFormData) => {
      const { error } = await api.from("break_types").insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["break_types"] });
    },
  });
}

export function useUpdateBreakType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: BreakTypeFormData & { id: string }) => {
      const { error } = await api.from("break_types").update(data).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["break_types"] });
      queryClient.invalidateQueries({ queryKey: ["shift_breaks"] });
    },
  });
}

export function useDeleteBreakType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from("break_types").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["break_types"] });
    },
  });
}

// =====================================================
// SHIFT BREAK MUTATIONS
// =====================================================

export function useCreateShiftBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ShiftBreakFormData) => {
      const { error } = await api.from("shift_breaks").insert([data]);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["shift_breaks", variables.shift_id] });
      queryClient.invalidateQueries({ queryKey: ["shifts", variables.shift_id, "breaks"] });
      queryClient.invalidateQueries({ queryKey: ["shifts_summary"] });
    },
  });
}

export function useDeleteShiftBreak() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, shiftId }: { id: string; shiftId: string }) => {
      const { error } = await api.from("shift_breaks").delete().eq("id", id);
      if (error) throw error;
      return { shiftId };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["shift_breaks", result.shiftId] });
      queryClient.invalidateQueries({ queryKey: ["shifts", result.shiftId, "breaks"] });
      queryClient.invalidateQueries({ queryKey: ["shifts_summary"] });
    },
  });
}

// =====================================================
// WORKER ASSIGNMENT MUTATIONS
// =====================================================

export function useCreateWorkerShiftAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: WorkerShiftAssignmentFormData) => {
      // End any current assignment for this worker
      const today = new Date().toISOString().split("T")[0];
      await api
        .from("worker_shift_assignments")
        .update({ end_date: data.effective_date })
        .eq("worker_id", data.worker_id)
        .is("end_date", null)
        .lt("effective_date", data.effective_date);

      // Create new assignment
      const { error } = await api.from("worker_shift_assignments").insert([data]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["worker_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["current_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["workers_without_shift"] });
    },
  });
}

export function useBulkCreateWorkerShiftAssignments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BulkShiftAssignmentFormData) => {
      // End current assignments for all selected workers
      for (const workerId of data.worker_ids) {
        await api
          .from("worker_shift_assignments")
          .update({ end_date: data.effective_date })
          .eq("worker_id", workerId)
          .is("end_date", null)
          .lt("effective_date", data.effective_date);
      }

      // Create new assignments
      const assignments = data.worker_ids.map((workerId) => ({
        worker_id: workerId,
        shift_id: data.shift_id,
        effective_date: data.effective_date,
      }));

      const { error } = await api.from("worker_shift_assignments").insert(assignments);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["worker_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["current_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["workers_without_shift"] });
    },
  });
}

export function useEndWorkerShiftAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, endDate }: { id: string; endDate: string }) => {
      const { error } = await api
        .from("worker_shift_assignments")
        .update({ end_date: endDate })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["worker_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["current_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["workers_without_shift"] });
    },
  });
}

export function useDeleteWorkerShiftAssignment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.from("worker_shift_assignments").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["worker_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["current_shift_assignments"] });
      queryClient.invalidateQueries({ queryKey: ["workers_without_shift"] });
    },
  });
}

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

export function formatTime(time: string): string {
  // Convert HH:MM:SS to HH:MM
  return time.slice(0, 5);
}

export function formatTimeRange(start: string, end: string, crossesMidnight: boolean): string {
  const startFormatted = formatTime(start);
  const endFormatted = formatTime(end);
  if (crossesMidnight) {
    return `${startFormatted} - ${endFormatted} (+1)`;
  }
  return `${startFormatted} - ${endFormatted}`;
}

export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins.toString().padStart(2, "0")}`;
}

export function calculateBreakTime(shiftStart: string, offsetMinutes: number): string {
  const [hours, minutes] = shiftStart.split(":").map(Number);
  const totalMinutes = hours * 60 + minutes + offsetMinutes;
  const breakHours = Math.floor(totalMinutes / 60) % 24;
  const breakMinutes = totalMinutes % 60;
  return `${breakHours.toString().padStart(2, "0")}:${breakMinutes.toString().padStart(2, "0")}`;
}
